/**
 * The citation grammar for agent responses: how sources are cited, and how raw-markdown
 * consumers read and replace citations.
 *
 * A citation id is `ref_` + 6 alphanumeric characters, minted by the backend. The agent
 * cites sources in two forms:
 *
 * 1. Reference marker — "[ref_ab12cd]": a bracketed id placed after the claim it
 *    supports. Renders as a numbered badge.
 * 2. Legal citation link — "[*Osborn v. Irwin*, 5 Cal. App. 4th 234 (1992)](ref_ab12cd)":
 *    a markdown link wrapping the authority text, targeting a bare citation id.
 *    Renders as the authority text itself, clickable.
 *
 * Every regex below composes from the one id pattern so the id format can only change
 * in one place. This grammar mirrors the backend source of truth in
 * paxton_app/agent/common/utils.py (_CITATION_ID_PATTERN / _CITATION_RE) — keep them in sync.
 */
const kCitationIdPattern = 'ref_[a-zA-Z0-9]{6}'

/**
 * Authority text may contain bracketed runs one level deep — CommonMark link parsing
 * (which the chat renderer's structural detection follows) accepts balanced brackets, and
 * real Bluebook citations use them: NY-reporter years ("100 N.Y.2d 1 [2003]") and quote
 * alterations ("[sic]", "[T]he"). Non-capturing so composed regexes keep their groups.
 */
const kAuthorityTextPattern = '(?:[^\\[\\]\\n]|\\[[^\\[\\]\\n]*\\])+'

/** Reference marker in text, e.g. "[ref_ab12cd]". */
export const kCitationMarkerRegex = new RegExp(`\\[${kCitationIdPattern}\\]`, 'g')

/** An <a> href that is exactly a bare citation id — a legal citation link after markdown parsing. */
export const kAgentLegalCitationLinkHrefRegex = new RegExp(`^${kCitationIdPattern}$`)

/**
 * Both forms in one alternation — the one tokenization every raw-markdown consumer shares
 * (mirrors the backend's _CITATION_RE). The link alternative comes first so a marker can
 * never match inside link syntax. Consumers never touch the capture groups directly:
 * read matches with readCitationMatch, or replace with replaceCitations.
 */
export const kCombinedCitationRegex = new RegExp(
  `\\[(?<authorityText>${kAuthorityTextPattern})\\]\\((?<linkId>${kCitationIdPattern})\\)|\\[(?<markerId>${kCitationIdPattern})\\]`,
  'g'
)

export type CitationToken = {
  citationId: string
  /** The authority text of a legal citation link; null for a reference marker. */
  authorityText: string | null
}

function tokenFromGroups(groups: Record<string, string | undefined> | undefined): CitationToken {
  if (groups?.linkId !== undefined) return { citationId: groups.linkId, authorityText: groups.authorityText ?? '' }
  return { citationId: groups?.markerId ?? '', authorityText: null }
}

/** Read a kCombinedCitationRegex match (e.g. from matchAll) as a citation token. */
export function readCitationMatch(match: RegExpMatchArray): CitationToken {
  return tokenFromGroups(match.groups)
}

/**
 * Citation ids in document order, one per occurrence (duplicates included), matching
 * both forms. Occurrence order is the contract consumers count on — it must agree with
 * the rehypeAgentCitations traversal so occurrence labels map to the right spans.
 * (The backend's set-valued equivalent is extract_citation_ids.)
 */
export function extractCitationIds(text: string): string[] {
  return Array.from(text.matchAll(kCombinedCitationRegex), (match) => readCitationMatch(match).citationId)
}

/**
 * Replace every citation token (both forms) in document order. The grammar owns the
 * iteration so consumers never depend on the alternation's group layout.
 */
export function replaceCitations(text: string, replacer: (token: string, citation: CitationToken) => string): string {
  return text.replace(kCombinedCitationRegex, (token: string, ...rest: unknown[]) => {
    // With named groups present, String.replace passes the groups object as the final argument.
    const groups = rest[rest.length - 1] as Record<string, string | undefined>
    return replacer(token, tokenFromGroups(groups))
  })
}
